import { Settings2, Rss, GripVertical, Plus, ChevronUp, ChevronDown } from 'lucide-react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
  useDndContext,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import {
  EDITORIAL_GRID_PRESET_CONFIG,
  type EditorialGridLayoutPreset,
} from '../page-blocks/definitions/editorialGrid'
import { useAdminArticles } from '../../api/hooks'
import { useState } from 'react'
import type { BlockCanvasState } from '../editor-shared/useBlockCanvas'
import type { CanvasSlot, ColKey, PickerMode } from '../editor-shared/canvasTypes'
import { COL_KEYS, COL_COLORS } from '../editor-shared/canvasTypes'
import type { BlockContent, WireArticle } from '../../api/types'
import type { PageBlockType } from '../../api/types'

// ─── DroppableColBlock ───────────────────────────────────────────────────────────────────

// Wrapper that registers a colBlock as a dnd-kit drop target so article drags
// can resolve to the correct column content key.
function DroppableColBlock({
  contentKey,
  isSelected,
  onClick,
  children,
}: {
  contentKey: string
  isSelected: boolean
  onClick: (e: React.MouseEvent) => void
  children: React.ReactNode
}) {
  // Only register as a drop target when an article drag is active.
  // During block reordering or shelf drags this droppable would otherwise
  // compete with (and win over) the top-level sortable rows.
  const { active } = useDndContext()
  const isArticleDrag = active?.data.current?.kind === 'article'
  const { setNodeRef, isOver } = useDroppable({ id: contentKey, disabled: !isArticleDrag })
  return (
    <div
      ref={setNodeRef}
      className={`cursor-pointer border-b border-line transition-colors ${
        isSelected ? 'ring-2 ring-inset ring-gray-800 dark:ring-gray-200' : 'hover:bg-fill'
      }${isOver ? ' outline outline-2 outline-offset-[-2px] outline-accent-primary-solid' : ''}`}
      onClick={onClick}
    >
      {children}
    </div>
  )
}

// ─── Feed summary badge row ────────────────────────────────────────────────────

function FeedSummary({ content }: { content: BlockContent }) {
  const feedType = content.feedType ?? 'latest'
  const params = content.feedParams ?? {}
  const chips: string[] = []
  if (params.limit) chips.push(`limit ${params.limit}`)
  if (params.topicSlug) chips.push(params.topicSlug)
  if (params.regionSlug) chips.push(params.regionSlug)
  return (
    <div className="flex flex-wrap items-center gap-2 p-3">
      <span className="flex items-center gap-1 rounded-full bg-info-fill px-2.5 py-0.5 text-[11px] font-semibold text-info-fg">
        <Rss size={10} aria-hidden="true" />
        {feedType.charAt(0).toUpperCase() + feedType.slice(1)}
      </span>
      {chips.map((chip) => (
        <span key={chip} className="rounded-full bg-fill px-2 py-0.5 text-[11px] text-fg-secondary">
          {chip}
        </span>
      ))}
    </div>
  )
}

// ─── Sortable article row (canvas) ───────────────────────────────────────────

function SortableArticleRow({
  id,
  isFeature,
  position,
  article,
  readOnly = false,
}: {
  id: string
  isFeature: boolean
  position: number   // 1-based display number for non-feature rows
  article: WireArticle | undefined
  readOnly?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2.5 px-3 py-2 ${readOnly ? 'cursor-default' : 'cursor-grab touch-none active:cursor-grabbing'} ${
        isDragging ? 'opacity-40' : ''
      } ${isFeature ? 'bg-info-fill' : ''}`}
      {...attributes}
      {...(readOnly ? {} : listeners)}
    >
      <span aria-hidden="true" className="shrink-0 text-fg-secondary opacity-30">
        <GripVertical size={11} />
      </span>
      {isFeature ? (
        <span className="shrink-0 rounded-sm bg-info-line px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-white">
          Feature
        </span>
      ) : (
        <span className="w-4 shrink-0 text-center text-[10px] font-bold text-fg-secondary">
          {position}
        </span>
      )}
      {article?.thumbnail?.presignedUrl ? (
        <img src={article.thumbnail.presignedUrl} alt="" aria-hidden="true" className="h-9 w-14 shrink-0 rounded object-cover" />
      ) : (
        <div className="h-9 w-14 shrink-0 rounded bg-fill" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        {article ? (
          <>
            <p className="truncate text-[11px] font-semibold text-fg">{article.title}</p>
            {(() => {
              const parts = [...(article.regions ?? []), ...(article.topics ?? [])]
              return parts.length > 0
                ? <p className="mt-0.5 truncate text-[10px] text-fg-secondary">{parts.join(' · ')}</p>
                : null
            })()}
          </>
        ) : (
          <p className="font-mono text-[10px] text-fg-secondary opacity-60">{id}</p>
        )}
      </div>
    </li>
  )
}

// ─── Sortable article list (canvas) ──────────────────────────────────────────

function SortableArticleList({
  ids,
  articleById,
  featureSplit,
  onReorder,
  readOnly = false,
}: {
  ids: string[]
  articleById: Record<string, WireArticle>
  featureSplit?: number
  onReorder: (newIds: string[]) => void
  readOnly?: boolean
}) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  if (ids.length === 0) {
    return (
      <div className="p-3 text-[11px] italic text-fg-secondary opacity-60">
        No articles assigned yet.
      </div>
    )
  }

  const splitAt = featureSplit ?? 0
  const isFeatureRail = featureSplit !== undefined
  const activeArticle = activeDragId ? articleById[activeDragId] : undefined

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex !== -1 && newIndex !== -1) {
      onReorder(arrayMove(ids, oldIndex, newIndex))
    }
  }

  const renderRow = (id: string, isFeature: boolean, position: number) => (
    <SortableArticleRow
      key={id}
      id={id}
      isFeature={isFeature}
      position={position}
      article={articleById[id]}
      readOnly={readOnly}
    />
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e: DragStartEvent) => setActiveDragId(String(e.active.id))}
      onDragEnd={readOnly ? () => {} : handleDragEnd}
      onDragCancel={() => setActiveDragId(null)}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {isFeatureRail ? (
          <div>
            <ul className="divide-y divide-line">
              {ids.slice(0, splitAt).map((id, i) => renderRow(id, true, i + 1))}
            </ul>
            {ids.slice(splitAt).length > 0 && (
              <>
                <div className="border-y border-line bg-surface px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
                  Rail
                </div>
                <ul className="divide-y divide-line">
                  {ids.slice(splitAt).map((id, i) => renderRow(id, false, i + 1))}
                </ul>
              </>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {ids.map((id, i) => renderRow(id, false, i + 1))}
          </ul>
        )}
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeDragId && (
          <li className="flex items-center gap-2.5 rounded border border-line-strong bg-canvas px-3 py-2 shadow-lg list-none">
            <GripVertical size={11} className="shrink-0 text-fg-secondary opacity-30" aria-hidden="true" />
            {activeArticle?.thumbnail?.presignedUrl ? (
              <img src={activeArticle.thumbnail.presignedUrl} alt="" aria-hidden="true" className="h-9 w-14 shrink-0 rounded object-cover" />
            ) : (
              <div className="h-9 w-14 shrink-0 rounded bg-fill" aria-hidden="true" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] font-semibold text-fg">{activeArticle?.title ?? activeDragId}</p>
            </div>
          </li>
        )}
      </DragOverlay>
    </DndContext>
  )
}

// ─── Unconfigured placeholder ─────────────────────────────────────────────────

function UnconfiguredPlaceholder({ blockLabel }: { blockLabel: string }) {
  return (
    <div className="flex items-center gap-2 p-3">
      <Settings2 size={14} className="shrink-0 text-fg-secondary opacity-50" aria-hidden="true" />
      <span className="text-[11px] text-fg-secondary">
        Click to configure content for this{' '}
        <span className="font-semibold">{blockLabel}</span> block.
      </span>
    </div>
  )
}

// ─── Sortable page row ────────────────────────────────────────────────────────
// Same structural pattern as SortableBlockRow in BlockCanvas, but the block
// body is content-aware (article lists, feed badges) rather than BlockPreview.

interface SortablePageRowProps {
  slot: CanvasSlot
  idx: number
  totalSlots: number
  isSelected: boolean
  isAssigningThisRow: boolean
  pickerMode: PickerMode
  selectedColKey: ColKey | null
  selectedColBlockId: string | null
  content: BlockContent | undefined
  articleById: Record<string, WireArticle>
  readOnly: boolean
  onSelect: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
  onColumnClick: (colKey: ColKey, colLabel: string) => void
  onColBlockSelect: (colKey: ColKey, blockId: string, blockType: PageBlockType) => void
  onRemoveColBlock: (colKey: ColKey, blockId: string) => void
  onMoveColBlock: (colKey: ColKey, blockId: string, direction: 'up' | 'down') => void
  contentMap: Record<string, BlockContent>
  onSetContent: (slotId: string, content: BlockContent) => void
}

function SortablePageRow({
  slot,
  idx,
  totalSlots,
  isSelected,
  isAssigningThisRow,
  pickerMode,
  selectedColKey,
  selectedColBlockId,
  content,
  articleById,
  readOnly,
  onSelect,
  onMoveUp,
  onMoveDown,
  onRemove,
  onColumnClick,
  onColBlockSelect,
  onRemoveColBlock,
  onMoveColBlock,
  contentMap,
  onSetContent,
}: SortablePageRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slot.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  }

  const def = PAGE_BLOCK_REGISTRY[slot.type] ?? { label: slot.type, mode: 'layout-only' as const, slots: [], configFields: [], defaultConfig: {} }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={`cursor-pointer overflow-hidden rounded border-2 transition-colors ${
        isSelected ? 'border-info-line' : 'border-line hover:border-line-strong'
      }${isAssigningThisRow ? ' ring-2 ring-blue-500 ring-offset-1' : ''}`}
    >
      {/* Chrome header — entire bar is the drag handle */}
      <div
        className={`flex items-center gap-2 border-b border-line px-2 py-1.5 ${readOnly ? 'cursor-default' : 'touch-none cursor-grab active:cursor-grabbing'} ${isSelected ? 'bg-info-fill' : 'bg-surface'}`}
        {...attributes}
        {...(readOnly ? {} : listeners)}
      >
        <span className="shrink-0 text-fg-secondary opacity-50" aria-hidden="true">
          <GripVertical size={14} />
        </span>
        <span className="w-5 text-center text-xs font-bold text-fg-secondary">{idx + 1}</span>
        <div className="flex flex-1 items-center gap-2">
          <span className={`text-xs font-semibold ${isSelected ? 'text-info-fg' : 'text-fg'}`}>{def.label}</span>

          {isSelected && (
            <span className="rounded-full bg-info-line px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-white">
              Editing
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Move block up"
            disabled={idx === 0}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onMoveUp() }}
            className="rounded px-1.5 py-0.5 text-xs text-fg-secondary hover:bg-solid hover:text-white disabled:opacity-30"
          >
            ↑
          </button>
          <button
            type="button"
            aria-label="Move block down"
            disabled={idx === totalSlots - 1}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onMoveDown() }}
            className="rounded px-1.5 py-0.5 text-xs text-fg-secondary hover:bg-solid hover:text-white disabled:opacity-30"
          >
            ↓
          </button>
          {!readOnly && (
            <button
              type="button"
              aria-label="Remove block"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onRemove() }}
              className="rounded px-1.5 py-0.5 text-xs text-fg-secondary hover:bg-red-9 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Block body — content-aware rendering (different from BlockCanvas's BlockPreview) */}
      {slot.type === 'editorial-grid' ? (() => {
        const preset = (slot.config.layoutPreset as EditorialGridLayoutPreset) ?? '3-equal'
        const presetCfg = EDITORIAL_GRID_PRESET_CONFIG[preset]
        const colKeys = COL_KEYS.slice(0, presetCfg.columns)
        const colLabels = ['Column 1', 'Column 2', 'Column 3']
        const isAssigningColKey = isAssigningThisRow
          ? (pickerMode as { kind: 'assign'; colKey: ColKey }).colKey
          : null
        return (
          <div className="overflow-x-auto">
          <div
            style={{ display: 'grid', gridTemplateColumns: presetCfg.gridTemplate, minWidth: `${presetCfg.columns * 180}px` }}
            className="divide-x divide-line"
          >
            {colKeys.map((colKey, i) => {
              const colBlocks = slot.columnBlocks?.[colKey] ?? []
              const color = COL_COLORS[i]
              const isThisColAssigning = isAssigningColKey === colKey
              return (
                <div key={colKey} className="flex min-w-0 flex-col overflow-hidden">
                  {/* Column header */}
                  <div className={`flex items-center gap-1.5 border-b px-2 py-1 ${isThisColAssigning ? 'border-info-line bg-info-fill' : 'border-line bg-surface'}`}>
                    <span className={`inline-block h-2 w-2 rounded-full ${color.dot}`} />
                    <span className={`text-[9px] font-bold uppercase tracking-widest ${color.label}`}>
                      {colLabels[i]}
                    </span>
                    <span className="ml-auto text-[9px] text-fg-secondary opacity-50">
                      {colBlocks.length} block{colBlocks.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {/* Sub-block list */}
                  {colBlocks.map((colBlock) => {
                    const isThisBlockSelected = isSelected && selectedColKey === colKey && selectedColBlockId === colBlock.id
                    const colContentKey = `${slot.id}:${colKey}:${colBlock.id}`
                    const colContent = contentMap[colContentKey]
                    const colDef = PAGE_BLOCK_REGISTRY[colBlock.type] ?? { label: colBlock.type, mode: 'layout-only' as const, slots: [], configFields: [], defaultConfig: {} }
                    return (
                      <DroppableColBlock
                        key={colBlock.id}
                        contentKey={colContentKey}
                        isSelected={isThisBlockSelected}
                        onClick={(e) => { e.stopPropagation(); onColBlockSelect(colKey, colBlock.id, colBlock.type) }}
                      >
                        <div className="flex items-center justify-between border-b border-line/40 px-2 py-0.5">
                          <span className="text-[9px] font-semibold text-fg-secondary">{colDef.label}</span>
                          {!readOnly && (
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onMoveColBlock(colKey, colBlock.id, 'up') }}
                                className="rounded px-0.5 py-0.5 text-fg-secondary hover:bg-fill disabled:opacity-30"
                                disabled={colBlocks.indexOf(colBlock) === 0}
                                aria-label="Move up"
                              ><ChevronUp size={11} /></button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onMoveColBlock(colKey, colBlock.id, 'down') }}
                                className="rounded px-0.5 py-0.5 text-fg-secondary hover:bg-fill disabled:opacity-30"
                                disabled={colBlocks.indexOf(colBlock) === colBlocks.length - 1}
                                aria-label="Move down"
                              ><ChevronDown size={11} /></button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onRemoveColBlock(colKey, colBlock.id) }}
                                className="ml-0.5 rounded px-1 text-[10px] text-fg-secondary hover:bg-red-9 hover:text-white"
                                aria-label="Remove block"
                              >✕</button>
                            </div>
                          )}
                        </div>
                        {!colContent ? (
                          <div className="flex items-center gap-2 p-2">
                            <Settings2 size={12} className="shrink-0 text-fg-secondary opacity-50" aria-hidden="true" />
                            <span className="text-[10px] text-fg-secondary">Click to configure {colDef.label}</span>
                          </div>
                        ) : colContent.source === 'feed' ? (
                          <div className="flex items-center gap-1.5 p-2">
                            <Rss size={10} className="shrink-0 text-fg-secondary" aria-hidden="true" />
                            <span className="text-[10px] text-fg-secondary capitalize">{colContent.feedType ?? 'latest'} feed</span>
                          </div>
                        ) : (
                          <SortableArticleList
                            ids={colContent.articleIds ?? []}
                            articleById={articleById}
                            onReorder={(newIds) => onSetContent(colContentKey, { ...colContent, articleIds: newIds })}
                            readOnly={readOnly}
                          />
                        )}
                      </DroppableColBlock>
                    )
                  })}
                  {/* Add block button */}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onColumnClick(colKey, colLabels[i]) }}
                      className={`flex items-center justify-center gap-1 py-2 text-[11px] font-semibold transition-colors ${isThisColAssigning ? 'bg-info-fill text-info-fg' : 'text-blue-11 hover:bg-blue-3'}`}
                    >
                      <Plus size={11} aria-hidden="true" />
                      Add Block
                    </button>
                  )}
                </div>
              )
            })}
          </div>
          </div>
        )
      })() : def.category === 'structural' ? (
        <div className="px-3 py-2 text-[11px] italic text-fg-secondary opacity-60">
          {def.description}
        </div>
      ) : !content ? (
        <UnconfiguredPlaceholder blockLabel={def.label} />
      ) : content.source === 'feed' ? (
        <FeedSummary content={content} />
      ) : (
        <SortableArticleList
          ids={content.articleIds ?? []}
          articleById={articleById}
          featureSplit={slot.type === 'feature-with-rail' ? 1 : undefined}
          onReorder={(newIds) => onSetContent(slot.id, { ...content, articleIds: newIds })}
          readOnly={readOnly}
        />
      )}
    </div>
  )
}

// ─── PageCanvas ───────────────────────────────────────────────────────────────

interface PageCanvasProps {
  canvas: BlockCanvasState
  contentMap: Record<string, BlockContent>
  onSetContent: (slotId: string, content: BlockContent) => void
  onRemoveBlock?: (slotId: string) => void
  readOnly?: boolean
}

export function PageCanvas({ canvas, contentMap, onSetContent, onRemoveBlock, readOnly = false }: PageCanvasProps) {
  const {
    slots,
    selectedId,
    pickerMode,
    selectedColKey,
    selectedColBlockId,
    setSelectedId,
    setSelectedColumnType,
    setSelectedColKey,
    setSelectedColBlockId,
    setPickerMode,
    moveBlock,
    removeBlock,
    removeColBlock,
    moveColBlock,
  } = canvas

  const { data: allDrafts = [] } = useAdminArticles()
  const articleById: Record<string, WireArticle> = Object.fromEntries(
    allDrafts.map((a) => [a.id, a]),
  )

  // Drop target for the empty-state placeholder — shelf drags need somewhere to land.
  const { setNodeRef: setEmptyDropRef, isOver: isOverEmpty } = useDroppable({ id: 'page-canvas' })

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-fg-secondary">
          Page Structure
        </h2>
        <span className="text-xs text-fg-secondary">
          {slots.length} block{slots.length !== 1 ? 's' : ''}
        </span>
      </div>

      {slots.length === 0 ? (
        <div
          ref={setEmptyDropRef}
          className={`flex h-48 items-center justify-center rounded border border-dashed text-sm transition-colors ${
            isOverEmpty
              ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
              : 'border-line text-fg-secondary'
          }`}
        >
          {isOverEmpty ? 'Drop to add' : 'Add rows from the left shelf to build this page.'}
        </div>
      ) : (
        <SortableContext items={slots.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {slots.map((slot, idx) => {
              const isSelected = slot.id === selectedId
              const isAssigningThisRow = pickerMode.kind === 'assign' && pickerMode.slotId === slot.id
              return (
                <SortablePageRow
                  key={slot.id}
                  slot={slot}
                  idx={idx}
                  totalSlots={slots.length}
                  isSelected={isSelected}
                  isAssigningThisRow={isAssigningThisRow}
                  pickerMode={pickerMode}
                  selectedColKey={selectedId === slot.id ? selectedColKey : null}
                  selectedColBlockId={selectedId === slot.id ? selectedColBlockId : null}
                  content={contentMap[slot.id]}
                  articleById={articleById}
                  readOnly={readOnly}
                  onSelect={() => {
                    setSelectedId(slot.id)
                    setSelectedColumnType(null)
                    setSelectedColKey(null)
                    setSelectedColBlockId(null)
                    if (pickerMode.kind === 'assign' && pickerMode.slotId !== slot.id) {
                      setPickerMode({ kind: 'add' })
                    }
                  }}
                  onMoveUp={() => moveBlock(slot.id, 'up')}
                  onMoveDown={() => moveBlock(slot.id, 'down')}
                  onRemove={() => { removeBlock(slot.id); onRemoveBlock?.(slot.id) }}
                  onColumnClick={(colKey, colLabel) =>
                    setPickerMode({ kind: 'assign', slotId: slot.id, colKey, colLabel, target: 'page' })
                  }
                  onColBlockSelect={(colKey, blockId, blockType) => {
                    setSelectedId(slot.id)
                    setSelectedColKey(colKey)
                    setSelectedColBlockId(blockId)
                    setSelectedColumnType(blockType)
                  }}
                  onRemoveColBlock={(colKey, blockId) => {
                    removeColBlock(slot.id, colKey, blockId)
                    if (selectedId === slot.id && selectedColKey === colKey && selectedColBlockId === blockId) {
                      setSelectedColumnType(null)
                      setSelectedColKey(null)
                      setSelectedColBlockId(null)
                    }
                  }}
                  onMoveColBlock={(colKey, blockId, direction) => moveColBlock(slot.id, colKey, blockId, direction)}
                  contentMap={contentMap}
                  onSetContent={onSetContent}
                />
              )
            })}
          </div>
        </SortableContext>
      )}
    </div>
  )
}
